import { Trans } from "../Translate";
import { useTranslation } from "react-i18next";
import React from "react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { PieChart } from "../PieChart";
import { ExpandIcon } from "./ExpandIcon";

import { DangerButton, PrimaryButton, SecondaryButton } from "../Button";
import {
  DragHandle,
  SubtleDropPlaceholder,
  projectItemsWithPlaceholder,
  useBoardDnD,
  useSortableItem,
} from "../utils/PragmaticDragAndDrop";

import { useForm } from "react-hook-form";
import classNames from "../utils/classnames";
import { State, TargetState, useGoalTargetListState } from "./useGoalTargetListState";

import { DeleteButton } from "./DeleteButton";
import { EditButton } from "./EditButton";
import { UpdateButton } from "./UpdateButton";

import { Textarea } from "../FormElements/Textarea";
import { Textfield } from "../FormElements/Textfield";
import { SwitchToggle } from "../SwitchToggle";
import { createTestId } from "../TestableElement";
import { calculateTargetProgress, formatValueAndUnit } from "../utils/goalTargetProgress";
import { formatNumber } from "../utils/formatting";

export namespace GoalTargetList {
  export type Target = {
    id: string;
    from: number;
    to: number;
    value: number;
    unit: string;
    name: string;
    index: number;
    mode: "view" | "update" | "edit" | "delete";
  };

  export type AddTargetFn = (inputs: {
    name: string;
    startValue: number;
    targetValue: number;
    unit: string;
  }) => Promise<{
    success: boolean;
    id: string;
  }>;

  export type DeleteTargetFn = (id: string) => Promise<boolean>;

  export type UpdateTargetFn = (inputs: {
    targetId: string;
    name: string;
    startValue: number;
    targetValue: number;
    unit: string;
  }) => Promise<boolean>;

  export type UpdateTargetValueFn = (id: string, value: number) => Promise<boolean>;
  export type UpdateTargetIndexFn = (id: string, index: number) => Promise<boolean>;

  export interface Props {
    targets: Target[];

    canReorder?: boolean;
    showEditButton?: boolean;
    showUpdateButton?: boolean;

    addActive?: boolean;
    onAddActiveChange?: (active: boolean) => void;

    addTarget: AddTargetFn;
    deleteTarget: DeleteTargetFn;
    updateTarget: UpdateTargetFn;
    updateTargetValue: UpdateTargetValueFn;
    updateTargetIndex: UpdateTargetIndexFn;
  }
}

export function GoalTargetList(props: GoalTargetList.Props) {
  const state = useGoalTargetListState(props);

  return <TargetList state={state} />;
}

function TargetList({ state }: { state: State }) {
  const containerId = React.useId();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const sorted = React.useMemo(() => {
    return [...state.targets].sort((a, b) => a.index - b.index);
  }, [state.targets]);

  const { draggedItemId, destination, draggedItemDimensions } = useBoardDnD(
    ({ itemId, destination }) => {
      state.reorder(itemId, destination.index);
    },
    { enabled: state.canReorder },
  );

  const { items: projectedTargets, placeholderIndex } = React.useMemo(
    () =>
      projectItemsWithPlaceholder({
        items: sorted,
        getId: (target) => target.id,
        draggedItemId,
        targetLocation: destination,
        containerId,
      }),
    [containerId, destination, draggedItemId, sorted],
  );

  const targetsWithIndex = React.useMemo(() => {
    return projectedTargets.map((target, index) => ({ ...target, index }));
  }, [projectedTargets]);

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element || !state.canReorder) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        containerId,
        index: targetsWithIndex.length,
      }),
    });
  }, [containerId, targetsWithIndex.length, state.canReorder]);

  const placeholderHeight = draggedItemDimensions?.height ?? null;

  return (
    <div>
      {state.targets.length > 0 && <TargetListHeader />}

      <div ref={containerRef}>
        {targetsWithIndex.map((target, index) => (
          <React.Fragment key={target.id}>
            {placeholderIndex === index && (
              <SubtleDropPlaceholder
                containerId={containerId}
                index={index}
                height={placeholderHeight}
                className="w-[calc(100% + 16px)] -ml-[16px]"
              />
            )}
            <TargetCard state={state} target={target} containerId={containerId} />
          </React.Fragment>
        ))}
        {placeholderIndex !== null && placeholderIndex === targetsWithIndex.length && (
          <SubtleDropPlaceholder
            containerId={containerId}
            index={targetsWithIndex.length}
            height={placeholderHeight}
            className="w-[calc(100% + 16px)] -ml-[16px]"
          />
        )}

        {state.addActive && <TargetAdd state={state} />}
      </div>
    </div>
  );
}

function TargetListHeader() {
  const { t } = useTranslation();
  const className = classNames(
    "flex items-center px-2 py-2",
    "border-t border-stroke-base font-semibold text-xs text-content-dimmed uppercase tracking-wider",
  );

  return (
    <div className={className}>
      <div className="flex-1">{t("Name")}</div>
      <div className="w-40 text-right">{t("Current value")}</div>
    </div>
  );
}

function TargetCard({ state, target, containerId }: { state: State; target: TargetState; containerId: string }) {
  if (target.mode === "view") {
    return <TargetView state={state} target={target} containerId={containerId} />;
  }

  if (target.mode === "update") {
    return <TargetUpdate state={state} target={target} />;
  }

  if (target.mode === "edit") {
    return <TargetEdit state={state} target={target} />;
  }

  if (target.mode === "delete") {
    return <TargetDelete state={state} target={target} />;
  }

  throw new Error(`Unknown mode: ${target.mode}`);
}

function TargetAdd({ state }: { state: State }) {
  const { t } = useTranslation();
  const [createMore, setCreateMore] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      from: "",
      to: "",
      unit: "",
    },
  });

  const onSubmit = (data: any) => {
    state.addTarget({
      name: data.name,
      from: parseFloat(data.from),
      to: parseFloat(data.to),
      unit: data.unit,
    });

    if (createMore) {
      reset();
    } else {
      state.cancelAdd();
    }
  };

  return (
    <InlineModal index={100}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Textarea
          testId="target-name"
          label={t("Name")}
          autoFocus
          placeholder={t("e.g. Increase monthly signup count")}
          error={errors.name?.message as string}
          {...register("name", { required: t("Can't be empty") })}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <Textfield
            testId={"target-from"}
            label={t("Start")}
            error={errors.from?.message as string}
            {...register("from", {
              required: t("Can't be empty"),
              validate: (v) => !isNaN(Number(v)) || t("Must be a number"),
            })}
            placeholder={t("e.g. 10000")}
          />
          <Textfield
            testId={"target-to"}
            label={t("Target")}
            error={errors.to?.message as string}
            {...register("to", {
              required: t("Can't be empty"),
              validate: (v) => !isNaN(Number(v)) || t("Must be a number"),
            })}
            placeholder={t("e.g. 15000")}
          />
          <Textfield
            testId={"target-unit"}
            label={t("Unit")}
            error={errors.unit?.message as string}
            {...register("unit", { required: t("Can't be empty") })}
            placeholder={t("e.g. users")}
          />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <SwitchToggle value={createMore} setValue={setCreateMore} label={t("Create more")} />
          <div className="flex-1"></div>
          <SecondaryButton size="xs" onClick={() => state.cancelAdd()} type="button" testId="cancel">
            {t("Cancel")}
          </SecondaryButton>
          <PrimaryButton size="xs" type="submit" testId="save">
            {t("Add Target")}
          </PrimaryButton>
        </div>
      </form>
    </InlineModal>
  );
}

function TargetUpdate({ state, target }: { state: State; target: TargetState }) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      value: target.value.toString(),
    },
  });

  const onSubmit = (data: any) => {
    state.updateTarget(target.id!, parseFloat(data.value));
  };

  return (
    <InlineModal index={target.index}>
      <p className="mb-4 font-medium">
        <Trans i18nKey="Update {{name}}" values={{ name: target.name }} />
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Textfield
          testId="target-value"
          autoFocus
          label={t("New Value")}
          error={errors.value?.message as string}
          addonRight={target.unit}
          textRight
          {...register("value", {
            required: t("Can't be empty"),
            validate: (v) => !isNaN(Number(v)) || t("Must be a number"),
          })}
        />
        <div className="flex items-center gap-2 justify-end mt-4">
          <SecondaryButton size="xs" onClick={() => state.cancelEdit(target.id)} type="button" testId="cancel">
            {t("Cancel")}
          </SecondaryButton>
          <PrimaryButton size="xs" type="submit" testId="save">
            {t("Save")}
          </PrimaryButton>
        </div>
      </form>
    </InlineModal>
  );
}

function TargetEdit({ state, target }: { state: State; target: TargetState }) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: target.name,
      from: target.from.toString(),
      to: target.to.toString(),
      unit: target.unit,
    },
  });

  const onSubmit = (data: any) => {
    state.saveEdit(target.id, {
      name: data.name,
      from: parseFloat(data.from),
      to: parseFloat(data.to),
      unit: data.unit,
    });
  };

  return (
    <InlineModal index={target.index}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Textarea
          label={t("Name")}
          autoFocus
          error={errors.name?.message as string}
          {...register("name", { required: t("Can't be empty") })}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <Textfield
            label={t("Start")}
            error={errors.from?.message as string}
            {...register("from", {
              required: t("Can't be empty"),
              validate: (v) => !isNaN(Number(v)) || t("Must be a number"),
            })}
          />
          <Textfield
            label={t("Target")}
            error={errors.to?.message as string}
            {...register("to", {
              required: t("Can't be empty"),
              validate: (v) => !isNaN(Number(v)) || t("Must be a number"),
            })}
          />
          <Textfield
            label={t("Unit")}
            error={errors.unit?.message as string}
            {...register("unit", { required: t("Can't be empty") })}
          />
        </div>
        <div className="flex items-center gap-2 justify-end mt-4">
          <SecondaryButton size="xs" onClick={() => state.cancelEdit(target.id)} type="button">
            {t("Cancel")}
          </SecondaryButton>
          <PrimaryButton size="xs" type="submit">
            {t("Save")}
          </PrimaryButton>
        </div>
      </form>
    </InlineModal>
  );
}

function TargetDelete({ state, target }: { state: State; target: TargetState }) {
  const { t } = useTranslation();
  return (
    <InlineModal index={target.index}>
      <div className="mb-2 font-bold">
        <Trans i18nKey="Delete {{name}} target?" values={{ name: target.name }} />
      </div>
      <p>{t("This will remove your target and all associated progress tracking.")}</p>

      <div className="flex items-center gap-2 justify-end mt-6">
        <SecondaryButton size="xs" onClick={() => state.cancelDelete(target.id)}>
          {t("Cancel")}
        </SecondaryButton>

        <DangerButton size="xs" onClick={() => state.deleteTarget(target.id)} testId="confirm">
          {t("Yes, Delete")}
        </DangerButton>
      </div>
    </InlineModal>
  );
}

function InlineModal({ index, children }: { index: number; children: React.ReactNode }) {
  const outerClass = "border-b border-stroke-base";

  const innerClass = classNames("border border-surface-outline rounded-lg p-8 px-6 shadow-lg", {
    "mb-4": index === 0,
    "my-4": index !== 0,
  });

  return (
    <div className={outerClass}>
      <div className={innerClass}>{children}</div>
    </div>
  );
}

function TargetView({ state, target, containerId }: { state: State; target: TargetState; containerId: string }) {
  const { ref, dragHandleRef, isDragging } = useSortableItem({
    itemId: target.id,
    disabled: !state.canReorder,
    index: target.index,
    containerId,
  });

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    state.toggleExpand(target.id!);
  };

  const outerClass = classNames("max-w-full py-2 px-px border-b border-stroke-base flex-1", {
    "border-t": target.index === 0,
  });

  const innerClass = classNames("grid gap-2 items-start cursor-pointer", {
    "grid-cols-[1fr_auto_auto_14px]": target.updateButtonVisible,
    "grid-cols-[1fr_auto_14px]": !target.updateButtonVisible,
  });

  const groupClass = classNames("group relative flex items-start", {
    "w-[calc(100% + 16px)] -ml-[16px]": state.canReorder,
    "opacity-40": isDragging,
  });

  return (
    <div
      className={groupClass}
      ref={ref as React.RefObject<HTMLDivElement>}
      data-test-id={createTestId("target", target.name)}
    >
      {state.canReorder && (
        <div ref={dragHandleRef as React.RefObject<HTMLDivElement>}>
          <DragHandle isDragging={isDragging} className="mr-1 mt-[14px]" />
        </div>
      )}

      <div className={outerClass}>
        <div onClick={toggleExpand} className={innerClass}>
          <TargetName target={target} truncate={!target.expanded} />
          <TargetValue target={target} />
          {target.updateButtonVisible && <UpdateButton state={state} target={target} />}
          <ExpandIcon expanded={target.expanded} onClick={toggleExpand} />
        </div>

        {target.expanded && <TargetDetails state={state} target={target} />}
      </div>
    </div>
  );
}

function TargetName({ target, truncate }: { target: TargetState; truncate?: boolean }) {
  const progress = calculateTargetProgress(target);
  const nameClass = classNames("flex gap-2 flex-1", { truncate: truncate });

  return (
    <div className={nameClass}>
      <div className="mt-1.5">
        <PieChart size={16} slices={[{ percentage: progress, color: "var(--color-green-500)" }]} />
      </div>

      <NameView name={target.name} truncate={truncate} />
    </div>
  );
}

function NameView({ name, truncate }: { name: string; truncate?: boolean }) {
  const className = classNames("font-medium mt-0.5", { truncate: truncate });

  return <div className={className}>{name}</div>;
}

function TargetValue({ target }: { target: GoalTargetList.Target }) {
  return (
    <div className="flex items-center">
      <div className="py-1 text-right text-sm font-extrabold">{formatValueAndUnit(target.value, target.unit)}</div>
    </div>
  );
}

function TargetDetails({ state, target }: { state: State; target: TargetState }) {
  const { t } = useTranslation();
  const { from, to, unit, value } = target;
  const progress = calculateTargetProgress(target, false);
  const decreasing = (from ?? 0) > (to ?? 0);

  return (
    <div className="text-sm ml-6 rounded-lg my-2">
      <div className="flex items-center gap-2">
        <div className="w-20 font-semibold">{t("Target")}</div>
        <div>
          {decreasing ? (
            <Trans
              i18nKey="From <from>{{from}}</from> down to <to>{{to}}</to>{{unit}}"
              values={{
                from: formatNumber(from ?? 0),
                to: formatNumber(to ?? 0),
                unit: unit === "%" ? "%" : ` ${unit}`,
              }}
              components={{ from: <span className="font-semibold" />, to: <span className="font-semibold" /> }}
            />
          ) : (
            <Trans
              i18nKey="From <from>{{from}}</from> to <to>{{to}}</to>{{unit}}"
              values={{
                from: formatNumber(from ?? 0),
                to: formatNumber(to ?? 0),
                unit: unit === "%" ? "%" : ` ${unit}`,
              }}
              components={{ from: <span className="font-semibold" />, to: <span className="font-semibold" /> }}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <div className="w-20 font-semibold">{t("Current")}</div>
        <div>
          {formatValueAndUnit(value, unit)}{" "}
          <span className={progress < 0 ? "text-red-500" : ""}>({progress.toFixed(1)}%)</span>
        </div>
      </div>

      {target.editButtonVisible && (
        <div className="flex items-center gap-2 mt-4">
          <EditButton state={state} target={target} />
          <DeleteButton state={state} target={target} />
        </div>
      )}
    </div>
  );
}
